import React from 'react';

/**
 * ErrorBoundary - captura errores de renderizado en modulos individuales.
 * Evita que un fallo en Finanzas, por ejemplo, rompa todo el app.
 * Uso: <ErrorBoundary module="Finanzas"><Finanzas /></ErrorBoundary>
 *
 * ── DOS PUBLICOS, DOS MENSAJES ──────────────────────────────────────────────
 *
 * `audience` distingue quien esta mirando, y no es un matiz de tono:
 *
 *   'team'    (por defecto) el equipo. Ve el mensaje tecnico porque le sirve
 *             para saber que paso sin abrir la consola.
 *
 *   'client'  un prospecto o un cliente. NUNCA ve el mensaje tecnico.
 *
 * La auditoria visual del 2026-09-18 encontro esto en la cotizacion publica:
 *
 *     Error en Cotizacion publica
 *     Cannot read properties of null (reading 'suggestedApplicationIds')
 *
 * Una propuesta comercial que ensena el nombre de una propiedad de JavaScript,
 * en ingles, a quien esta decidiendo si paga varios miles de dolares. El fallo
 * de fondo se arregla donde este; esto asegura que, ocurra lo que ocurra, el
 * cliente no lea nunca la tripa.
 *
 * ── NO ES ESCONDER EL ERROR ────────────────────────────────────────────────
 *
 * El error sigue yendo entero a `console.error`, que es donde lo recoge la
 * observabilidad. Lo que cambia es el DOM: con `audience="client"` el mensaje
 * tecnico no se pinta, ni siquiera oculto. Un nodo invisible sigue estando en
 * el HTML, y el HTML se comparte en capturas.
 *
 * Y no se registra nada del contenido: ni nombre de cliente, ni correo, ni
 * payload de la cotizacion. Solo el error y su traza de componentes.
 */

const ES_ERROR_DE_VERSION = /loading chunk|chunkloaderror|failed to fetch dynamically imported module|importing a module script failed/i;

/** Lo que lee un cliente. Cuatro casos, ninguno con jerga. */
function mensajeParaCliente(esErrorDeVersion) {
  if (esErrorDeVersion) {
    return {
      titulo: 'Hay una versión más reciente',
      cuerpo: 'Actualiza para seguir viendo tu propuesta. No se pierde nada de lo que hayas elegido.',
      accion: 'Actualizar',
    };
  }
  return {
    titulo: 'No pudimos mostrar esta parte',
    cuerpo: 'Es un problema nuestro, no tuyo. Vuelve a intentarlo; si sigue igual, escríbenos y lo revisamos contigo.',
    accion: 'Reintentar',
  };
}

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Observabilidad: el error entero, sin datos del cliente.
    console.error(`[ErrorBoundary:${this.props.module || 'App'}]`, error, info);
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    const module = this.props.module || 'este modulo';
    const message = this.state.error?.message || '';
    const isChunkError = ES_ERROR_DE_VERSION.test(message);
    const paraCliente = this.props.audience === 'client';
    const humano = paraCliente ? mensajeParaCliente(isChunkError) : null;

    const reintentar = () => {
      if (isChunkError) {
        window.location.reload();
        return;
      }
      this.setState({ hasError: false, error: null });
    };

    return (
      <div style={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 40, flexDirection: 'column', gap: 16,
      }}>
        <div style={{ fontSize: 32 }}>!</div>
        <div style={{
          fontWeight: 700, fontSize: 16, color: 'var(--text)',
          fontFamily: 'var(--font-display)',
        }}>
          {paraCliente ? humano.titulo : `Error en ${module}`}
        </div>
        <div style={{ fontSize: paraCliente ? 13 : 12, color: 'var(--text-d)', textAlign: 'center', maxWidth: 400, lineHeight: 1.6 }}>
          {/* CLIENTE: jamas `message`. Ni aqui, ni oculto, ni en un title. */}
          {paraCliente
            ? humano.cuerpo
            : (isChunkError
              ? 'Hay una nueva version disponible. Actualiza para continuar.'
              : (message || 'Ocurrio un error inesperado.'))}
        </div>
        <button
          onClick={reintentar}
          style={{
            background: 'var(--gold)', color: 'var(--accent-contrast)', border: 'none',
            borderRadius: 8, padding: '10px 20px', minHeight: 44, fontSize: 12, fontWeight: 700,
            cursor: 'pointer', fontFamily: 'inherit',
          }}>
          {paraCliente ? humano.accion : (isChunkError ? 'Actualizar version' : 'Reintentar')}
        </button>
        {!paraCliente && (
          <div style={{ fontSize: 10, color: 'var(--text-d)' }}>
            {isChunkError ? 'La app cargara el bundle mas reciente.' : 'Si el error persiste, recarga la pagina o contacta al equipo tecnico.'}
          </div>
        )}
      </div>
    );
  }
}
